import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  useEventCallback,
} from '@mui/material';
import ChooseDate from 'shell/components/shared/choice-date';
import { PmiButton } from 'shell/components/shared/button';
import ListGender from 'shell/components/shared/gender';
import { memo, useCallback, useMemo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import AireList from 'system/components/shared/aire-list';
import Foreign from './foreign';
import Italian from './italian';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import { FormikErrors, FormikTouched } from 'formik';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ForeignForm } from 'shell/view-models/foreign-form';
import { DateTime } from 'luxon';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
// import NotCertifiableAnprList from 'shell/components/shared/not-certifiable-anpr-list';
import { IdFlagFiscalCode } from 'subscription/constants/id-flag-fiscal-code';
import { SubscriptionAccordionGenerality } from 'subscription/view-models/common-interface-subscription';

interface FormGeneralityProps {
  isEditMode?: boolean;
  disabled?: boolean;
  generality?: SubscriptionAccordionGenerality;
  editDataControlFlag?: string;
  person?: AccordionPersonForm;
  errorsGenerality?: FormikErrors<SubscriptionAccordionGenerality>;
  touchedGenerality?: FormikTouched<SubscriptionAccordionGenerality>;
  errorsPerson?: FormikErrors<AccordionPersonForm>;
  touchedPerson?: FormikTouched<AccordionPersonForm>;
  onChangePerson?: (
    key: keyof AccordionPersonForm,
    value: AccordionPersonForm[keyof AccordionPersonForm]
  ) => void;
  onChangeGenerality: (
    key: keyof SubscriptionAccordionGenerality,
    value: SubscriptionAccordionGenerality[keyof SubscriptionAccordionGenerality]
  ) => void;
  resetForm: () => void;
  onChangeValues: (values: SubscriptionAccordionGenerality) => void;
  isCohabitation?: boolean;
  isAire?: boolean;
  nonResident?: boolean;
  isFromCivilStatus?: boolean;
  isDeathPerson?: boolean;
}

const FormGeneralityComponent = ({
  isEditMode,
  disabled = false,
  person,
  generality,
  errorsPerson,
  errorsGenerality,
  onChangeGenerality,
  onChangeValues,
  onChangePerson,
  isCohabitation,
  resetForm,
  isAire = false,
  nonResident,
  isFromCivilStatus,
  editDataControlFlag,
  isDeathPerson = false,
}: FormGeneralityProps): React.ReactElement => {
  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChangeGenerality('italian', {
        ...(generality || {}).italian,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChangeGenerality('foreign', {
        ...(generality || {}).foreign,
        [key]: value,
      });
    },
    [generality, onChangeGenerality]
  );

  const handleListGenderChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChangeGenerality('gender', event.target.value);
  });

  const handleAireListChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChangeGenerality('aire', event.target.value);
  });

  const handleChooseDateChange = useEventCallback(
    (eventDate: DateTime | null, choice?: Array<DatePickerView>) => {
      onChangeValues({ birthDate: eventDate, birthChoiceDate: choice });
    }
  );

  const disabledFiscalCode: boolean = useMemo(() => {
    if (isEditMode) {
      return false;
    }

    return editDataControlFlag === IdFlagFiscalCode.Attribuzione;
  }, [isFromCivilStatus, isEditMode, disabled, editDataControlFlag]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Codice fiscale"
          aria-label="Codice fiscale"
          id="fiscal-code"
          value={person?.fiscalCode?.toUpperCase() || generality?.fiscalCode?.toUpperCase() || ''}
          onChange={(event) => {
            if (onChangePerson) {
              onChangePerson('fiscalCode', event.target.value);
            }
            if (onChangeGenerality) {
              onChangeGenerality('fiscalCode', event.target.value);
            }
          }}
          helperText={errorsGenerality?.fiscalCode}
          error={Boolean(errorsGenerality?.fiscalCode) || Boolean(errorsPerson?.fiscalCode)}
          fullWidth
          disabled={disabledFiscalCode}
          inputProps={{ maxLength: '16' }}
        />
      </Grid>
      <Grid
        item
        container
        spacing={0}
        columnSpacing={{ xs: 0, md: 1 }}
        rowSpacing={{ xs: 1, md: 0 }}
        xs={12}
        md={10}
        alignItems="center"
      >
        <Grid item xs={12} md={9}>
          <PmiTextField
            label={person?.withoutSurname || generality?.withoutSurname ? 'Cognome' : 'Cognome *'}
            aria-label="Cognome"
            id="surname"
            fullWidth
            disabled={isEditMode ? false : disabled}
            helperText={errorsGenerality?.surname}
            error={Boolean(errorsGenerality?.surname) || Boolean(errorsPerson?.surname)}
            value={person?.surname || generality?.surname || ''}
            onChange={(event) => {
              if (onChangePerson) {
                onChangePerson('surname', event.target.value);
              }
              if (onChangeGenerality) {
                onChangeGenerality('surname', event.target.value);
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutSurname-replica"
                checked={person?.withoutSurname || generality?.withoutSurname || false}
                onChange={() => {
                  if (onChangePerson) {
                    onChangePerson('withoutSurname', !person?.withoutSurname || false);
                  }
                  if (onChangeGenerality) {
                    onChangeGenerality('withoutSurname', !generality?.withoutSurname || false);
                  }
                }}
                inputProps={{ 'aria-label': 'Persona senza cognome' }}
                disabled={isEditMode ? false : disabled}
              />
            }
            label="Persona senza cognome"
          />
        </Grid>
      </Grid>
      <Grid
        item
        container
        spacing={0}
        columnSpacing={{ xs: 0, md: 1 }}
        rowSpacing={{ xs: 1, md: 0 }}
        xs={12}
        md={10}
        alignItems="center"
      >
        <Grid item xs={12} md={9}>
          <PmiTextField
            label={person?.withoutName || generality?.withoutName ? 'Nome' : 'Nome *'}
            aria-label="Nome"
            id="name"
            fullWidth
            disabled={isEditMode ? false : disabled}
            helperText={errorsGenerality?.name}
            error={Boolean(errorsGenerality?.name) || Boolean(errorsPerson?.name)}
            value={person?.name || generality?.name || ''}
            onChange={(event) => {
              if (onChangePerson) {
                onChangePerson('name', event.target.value);
              }
              if (onChangeGenerality) {
                onChangeGenerality('name', event.target.value);
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutName-replica"
                checked={person?.withoutName || generality?.withoutName || false}
                onChange={() => {
                  if (onChangePerson) {
                    onChangePerson('withoutName', !person?.withoutName || false);
                  }
                  if (onChangeGenerality) {
                    onChangeGenerality('withoutName', !generality?.withoutName || false);
                  }
                }}
                inputProps={{ 'aria-label': 'Persona senza nome' }}
                disabled={isEditMode ? false : disabled}
              />
            }
            label="Persona senza nome"
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid
        item
        xs={12}
        container
        spacing={0}
        columnSpacing={{ xs: 0, md: 1 }}
        rowSpacing={{ xs: 1, md: 0 }}
      >
        <Grid item xs={12} md={4}>
          <ListGender
            required
            value={generality?.gender || ''}
            onChange={handleListGenderChange}
            disabled={isEditMode ? false : disabled}
            error={Boolean(errorsGenerality?.gender)}
          />
        </Grid>
        {nonResident ? undefined : (
          <Grid item xs={12} md={4}>
            <AireList
              required
              value={generality?.aire || ''}
              onChange={handleAireListChange}
              disabled={isCohabitation || isAire || disabled}
              error={Boolean(errorsGenerality?.aire)}
            />
          </Grid>
        )}
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} alignItems="center">
        <ChooseDate
          selected={generality?.birthDate || null}
          onChange={handleChooseDateChange}
          choice={generality?.birthChoiceDate}
          disabled={isEditMode ? false : disabled}
          required
          helperText={errorsGenerality?.birthDate}
          error={Boolean(errorsGenerality?.birthDate)}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Nascita"
            id="isForeign"
            value={generality?.isForeign || 'false'}
            onChange={() =>
              onChangeValues({
                isForeign: !generality?.isForeign,
                italian: { municipality: null },
                foreign: { state: null },
              })
            }
          >
            <FormControlLabel
              value="false"
              control={<Radio />}
              label="Nascita Italia"
              disabled={isEditMode ? false : disabled}
            />
            <FormControlLabel
              value="true"
              control={<Radio />}
              label="Nascita Estero"
              disabled={isEditMode ? false : disabled}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!generality?.isForeign ? (
        <Italian
          disabled={disabled}
          isEditMode={isEditMode}
          values={generality?.italian || undefined}
          birthday={generality?.birthDate}
          onChange={onFieldItalianChange}
          errors={errorsGenerality?.italian as FormikErrors<ItalianForm>}
        />
      ) : (
        <Foreign
          disabled={disabled}
          isEditMode={isEditMode}
          values={generality?.foreign || undefined}
          onChange={onFieldForeignChange}
          errors={errorsGenerality?.foreign as FormikErrors<ForeignForm>}
        />
      )}
      {nonResident || isDeathPerson ? (
        <Grid
          item
          container
          spacing={0}
          columnSpacing={{ xs: 0, md: 1 }}
          rowSpacing={{ xs: 1, md: 0 }}
          xs={12}
          md={10}
          alignItems="center"
        >
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  id="deathPerson"
                  checked={generality?.deathPerson || false}
                  onChange={() => {
                    onChangeGenerality('deathPerson', !generality?.deathPerson || false);
                  }}
                  inputProps={{ 'aria-label': 'Nato Morto' }}
                  disabled={disabled}
                />
              }
              label="Nato Morto"
            />
          </Grid>
        </Grid>
      ) : undefined}
      <Grid item xs={12} md={2}>
        <PmiButton
          color="cdmBrandThird"
          variant="outlined"
          disabled={disabled}
          fullWidth
          onClick={resetForm}
        >
          Ripulisci
        </PmiButton>
      </Grid>
    </Grid>
  );
};

FormGeneralityComponent.displayName = 'Form generality component';

const FormGenerality = memo(FormGeneralityComponent);

export default FormGenerality;
